import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { tasksService } from "@/services/tasksService";
import { BulkUpdateTasksDto, Staff, Task, UpdateTaskDto } from "@/types/tasks";
import { requestKeys } from "./useRequests";
import { IGetRequestsResponse } from "@/services/interfaces/IRequestService";
import { Request } from "@/types/request";
import { reservationKeys } from "@/hooks/useReservations";
import { settlementKeys } from "@/components/pages/settlement/hooks/useSettlement";

type UpdateTaskMutationArgs = {
  id: string;
  data: UpdateTaskDto;
  optimisticStaff?: Staff | null;
};

const normalizeUpdatedTask = (
  updatedTask: Task,
  data: UpdateTaskDto,
  optimisticStaff?: Staff | null,
): Task => {
  if (data.staffId) {
    return {
      ...updatedTask,
      staffId: data.staffId,
      teamStaffId: null,
      team: null,
      teamStaff: null,
      staff: optimisticStaff || updatedTask.staff,
    };
  }

  if (data.teamStaffId) {
    return {
      ...updatedTask,
      teamStaffId: data.teamStaffId,
      staffId: null,
      staff: null,
    };
  }

  return updatedTask;
};

const syncUpdatedTaskInRequest = (request: Request, updatedTask: Task) => {
  const tasks = request.tasks?.map((task) =>
    task.id === updatedTask.id ? updatedTask : task,
  );

  if (!tasks) {
    return request;
  }

  return {
    ...request,
    tasks,
  };
};

export const taskKeys = {
  all: ["tasks"] as const,
  lists: () => [...taskKeys.all, "list"] as const,
  details: () => [...taskKeys.all, "detail"] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateTaskRelatedQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: requestKeys.all });
  queryClient.invalidateQueries({ queryKey: reservationKeys.all });
  queryClient.invalidateQueries({ queryKey: settlementKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: ["staff"] });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: UpdateTaskMutationArgs) =>
      tasksService.update(id, data),
    onSuccess: (updatedTask, { data, optimisticStaff }) => {
      const normalizedTask = normalizeUpdatedTask(
        updatedTask,
        data,
        optimisticStaff,
      );

      queryClient.setQueryData(
        taskKeys.detail(normalizedTask.id),
        normalizedTask,
      );
      queryClient.setQueriesData(
        { queryKey: requestKeys.details() },
        (request?: Request) =>
          request ? syncUpdatedTaskInRequest(request, normalizedTask) : request,
      );
      queryClient.setQueriesData(
        { queryKey: requestKeys.lists() },
        (response?: IGetRequestsResponse) =>
          response
            ? {
                ...response,
                data: response.data.map((request) =>
                  syncUpdatedTaskInRequest(request, normalizedTask),
                ),
              }
            : response,
      );
      invalidateTaskRelatedQueries(queryClient);
    },
  });
};

export const useBulkUpdateTasks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: Partial<BulkUpdateTasksDto> }) =>
      tasksService.bulkUpdate(data),
    onSuccess: () => {
      invalidateTaskRelatedQueries(queryClient);
    },
  });
};
